import { Link } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import { useMttLocationsQuery } from '../../graphql'
import TableHeaderCell from './components/TableHeaderCell'

type MttRow = {
	id: string
	name: string
	active: boolean
}

export default function MttManagement() {
	const { data, loading, error } = useMttLocationsQuery( {
		variables: { paging: { first: 200 } },
		fetchPolicy: 'network-only',
	} )

	const mtts: MttRow[] = data?.branches.edges.map( ( edge ) => ( {
		id: edge.node.id,
		name: edge.node.name,
		active: edge.node.active,
	} ) ) ?? []

	return (
		<Box sx={{ p: 3, maxWidth: 900 }}>
			<Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
				<LocalShippingIcon sx={{ fontSize: 28, color: 'primary.main' }} />
				<Box>
					<Typography variant="h5" fontWeight={700}>Mobile Tool Trailers</Typography>
					<Typography variant="body2" color="text.secondary">
						Select an MTT to review its inventory and transfer items to a Distribution Center.
					</Typography>
				</Box>
			</Stack>

			{loading && (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
					<CircularProgress />
				</Box>
			)}

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					Failed to load MTTs: {error.message}
				</Alert>
			)}

			{!loading && mtts.length === 0 && !error && (
				<Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
					<Typography color="text.secondary">No mobile tool trailers found.</Typography>
				</Paper>
			)}

			{mtts.length > 0 && (
				<TableContainer component={Paper} variant="outlined">
					{/* Explicit size preserves prior rendering; the app-wide MuiTable default is now `small`. */}
					<Table size="medium">
						<TableHead>
							<TableRow sx={{ bgcolor: 'grey.50' }}>
								<TableHeaderCell>MTT Name</TableHeaderCell>
								<TableHeaderCell>Status</TableHeaderCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{mtts.map( ( mtt ) => (
								<TableRow key={mtt.id} hover>
									<TableCell>
										<Stack direction="row" spacing={1} alignItems="center">
											<LocalShippingIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
											<Link
												to={`/dc-admin/mtt/${mtt.id}`}
												style={{ color: 'inherit', textDecoration: 'none' }}
											>
												<Typography
													variant="body2"
													fontWeight={500}
													sx={{ 'color': 'primary.main', '&:hover': { textDecoration: 'underline' } }}
												>
													{mtt.name}
												</Typography>
											</Link>
										</Stack>
									</TableCell>
									<TableCell>
										<Chip
											label={mtt.active ? 'Active' : 'Inactive'}
											color={mtt.active ? 'success' : 'default'}
											size="small"
										/>
									</TableCell>
								</TableRow>
							) )}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
	)
}
